import { useEffect, useMemo, useState } from 'react';
import * as THREE from 'three';
import { Line } from '@react-three/drei';
import { ThreeEvent } from '@react-three/fiber';
import { useSystemVarsStore } from '../state/useSystemVarsStore';

/**
 * Underlay & scan-data entity renderers (Faz 9.7 / 9.9).
 *
 * ImageUnderlayEntity: PDF/raster underlay — textured plane sized in world
 * units, optional frame (FRAMES sysvar semantics live in EntityLayer), and
 * a normalized clip (rect or polygon, 0..1 in entity space) drawn through
 * a shape geometry so the texture UVs remap automatically.
 *
 * PointCloudEntity: display-only sampled scan (max 200k points enforced at
 * the runner) — one `points` draw call, vertex colors when present, no
 * snapping and no editing (OSNAP Node is deliberately off; plan §3.4).
 */

export interface ImageClip {
    kind: 'rect' | 'poly';
    /** kind === 'rect': [x0, y0, x1, y1] normalized 0..1 (y up). */
    rect?: [number, number, number, number];
    /** kind === 'poly': ring of normalized corner points. */
    points?: [number, number][];
}

export interface ImageUnderlayEntityProps {
    src: string;
    position: [number, number, number];
    width: number;
    height: number;
    clip: ImageClip | null;
    /** Per-entity frame flag (data.frame); the FRAMES sysvar decides. */
    frameFlag: boolean;
    frameColor: string;
    entityId: string;
    isSelected: boolean;
    onClick: (e: ThreeEvent<MouseEvent>) => void;
}

export function ImageUnderlayEntity({
    src, position, width, height, clip, frameFlag, frameColor, entityId, isSelected, onClick,
}: ImageUnderlayEntityProps) {
    // FRAMES semantics (OCS): 0 hides all frames, 1 respects the entity
    // flag, 2 forces every frame on.
    const frames = useSystemVarsStore((s) => s.FRAMES);
    const showFrame = (frames ?? 1) !== 0 && ((frames ?? 1) === 2 || frameFlag);
    const [texture, setTexture] = useState<THREE.Texture | null>(null);
    useEffect(() => {
        let disposed = false;
        const loader = new THREE.TextureLoader();
        const tex = loader.load(src, (t) => {
            if (disposed) {
                t.dispose();
                return;
            }
            t.colorSpace = THREE.SRGBColorSpace;
            setTexture(t);
        });
        return () => {
            disposed = true;
            tex.dispose();
            setTexture(null);
        };
    }, [src]);

    // Clip region → THREE.Shape in entity-local normalized space; the shape
    // geometry's UVs are the shape's own xy coordinates, so the texture is
    // mapped with repeat/offset back to the full image frame.
    const shape = useMemo(() => {
        if (!clip) return null;
        if (clip.kind === 'rect' && clip.rect) {
            const [x0, y0, x1, y1] = clip.rect;
            const s = new THREE.Shape();
            s.moveTo(x0 * width, y0 * height);
            s.lineTo(x1 * width, y0 * height);
            s.lineTo(x1 * width, y1 * height);
            s.lineTo(x0 * width, y1 * height);
            s.closePath();
            return s;
        }
        if (clip.kind === 'poly' && clip.points && clip.points.length >= 3) {
            const s = new THREE.Shape();
            s.moveTo(clip.points[0][0] * width, clip.points[0][1] * height);
            for (let i = 1; i < clip.points.length; i++) {
                s.lineTo(clip.points[i][0] * width, clip.points[i][1] * height);
            }
            s.closePath();
            return s;
        }
        return null;
    }, [clip, width, height]);

    const mappedTexture = useMemo(() => {
        if (!texture) return null;
        const t = texture.clone();
        t.needsUpdate = true;
        t.wrapS = t.wrapT = THREE.ClampToEdgeWrapping;
        if (shape) {
            // shape xy is entity-local; uv = shapeXY → (xy - origin)/size
            t.repeat.set(1 / width, 1 / height);
            t.offset.set(-position[0] / width, -position[1] / height);
        }
        return t;
    }, [texture, shape, width, height, position]);

    const fullRing = useMemo<[number, number, number][]>(() => [
        [position[0], position[1], position[2] + 0.002],
        [position[0] + width, position[1], position[2] + 0.002],
        [position[0] + width, position[1] + height, position[2] + 0.002],
        [position[0], position[1] + height, position[2] + 0.002],
        [position[0], position[1], position[2] + 0.002],
    ], [position, width, height]);

    if (!mappedTexture) return null;

    return (
        <group key={entityId} onClick={onClick}>
            {shape ? (
                <mesh position={[position[0], position[1], position[2] + 0.001]}>
                    <shapeGeometry args={[shape]} />
                    <meshBasicMaterial
                        map={mappedTexture}
                        transparent
                        toneMapped={false}
                        side={THREE.DoubleSide}
                        depthWrite={false}
                    />
                </mesh>
            ) : (
                <mesh position={[position[0] + width / 2, position[1] + height / 2, position[2] + 0.001]}>
                    <planeGeometry args={[width, height]} />
                    <meshBasicMaterial
                        map={mappedTexture}
                        transparent
                        toneMapped={false}
                        side={THREE.DoubleSide}
                        depthWrite={false}
                    />
                </mesh>
            )}
            {showFrame && (
                <Line
                    points={fullRing}
                    color={frameColor}
                    lineWidth={isSelected ? 2.5 : 1}
                    dashed={false}
                />
            )}
        </group>
    );
}

export interface PointCloudEntityProps {
    points: number[];
    colors: number[];
    pointSize: number;
    entityId: string;
    isSelected: boolean;
    onClick: (e: ThreeEvent<MouseEvent>) => void;
}

export function PointCloudEntity({
    points, colors, pointSize, entityId, isSelected, onClick,
}: PointCloudEntityProps) {
    const geometry = useMemo(() => {
        const g = new THREE.BufferGeometry();
        g.setAttribute('position', new THREE.BufferAttribute(new Float32Array(points), 3));
        if (colors.length === points.length && colors.length > 0) {
            g.setAttribute('color', new THREE.BufferAttribute(new Float32Array(colors), 3));
        }
        return g;
        // Point data is written once at attach time; length keys cover it.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [entityId, points.length, colors.length]);

    useEffect(() => () => geometry.dispose(), [geometry]);
    const hasColors = colors.length === points.length && colors.length > 0;

    return (
        <points geometry={geometry} key={entityId} onClick={onClick}>
            <pointsMaterial
                size={pointSize}
                vertexColors={hasColors}
                color={hasColors ? undefined : (isSelected ? '#00aaff' : '#cccccc')}
                sizeAttenuation
                transparent
                opacity={0.9}
            />
        </points>
    );
}
